import React from 'react';
import { getWidaColor, getLevelName, MODALITY_INFO } from '@/utils/proficiencyHelpers';

/**
 * ProficiencyLevelRing — A circular visual indicator for a single modality's WIDA level.
 * @param {string} modality - listening, speaking, reading, or writing
 * @param {number} level - WIDA level 1-6
 * @param {number} size - diameter in pixels (default 56)
 * @param {boolean} showLabel - show modality label below (default true)
 */
export const ProficiencyLevelRing = ({ modality, level = 1, size = 56, showLabel = true }) => {
  const lvl = Math.min(6, Math.max(1, Math.round(level || 1)));
  const color = getWidaColor(lvl);
  const pct = (lvl / 6) * 100;
  const radius = (size - 8) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (pct / 100) * circumference;
  const info = MODALITY_INFO[modality] || {};

  return (
    <div className="flex flex-col items-center gap-1">
      <div className="relative" style={{ width: size, height: size }}>
        <svg width={size} height={size} className="-rotate-90">
          <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="#e5e7eb" strokeWidth="4" />
          <circle
            cx={size / 2} cy={size / 2} r={radius} fill="none"
            stroke={color.ring} strokeWidth="4" strokeLinecap="round"
            strokeDasharray={circumference} strokeDashoffset={offset}
            style={{ transition: 'stroke-dashoffset 0.6s ease' }}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className={`text-base font-black ${color.text}`} style={{ fontFamily: 'Outfit, sans-serif' }}>{lvl}</span>
        </div>
      </div>
      {showLabel && (
        <div className="text-center">
          <div className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider">{info.short || modality?.[0]?.toUpperCase()}</div>
        </div>
      )}
    </div>
  );
};

/**
 * ProficiencyLevelRings — Shows all 4 modality rings in a row.
 * @param {object} levels - { listening: 3, speaking: 2, reading: 4, writing: 1 }
 * @param {number} size - ring size
 */
export const ProficiencyLevelRings = ({ levels = {}, size = 48 }) => {
  return (
    <div className="flex items-center gap-3" data-testid="wida-level-rings">
      {['listening', 'speaking', 'reading', 'writing'].map(mod => (
        <ProficiencyLevelRing key={mod} modality={mod} level={levels[mod] || 0} size={size} />
      ))}
    </div>
  );
};

/**
 * ProficiencyCompositeBar — Shows a horizontal bar with the composite WIDA level and name.
 * @param {number} level - WIDA composite level 1-6
 * @param {boolean} compact - smaller display
 */
export const ProficiencyCompositeBar = ({ level = 1, compact = false }) => {
  const lvl = Math.min(6, Math.max(1, Math.round(level || 1)));
  const color = getWidaColor(lvl);
  const name = getLevelName(lvl);
  const pct = (lvl / 6) * 100;

  return (
    <div className={`flex items-center gap-3 ${compact ? '' : 'p-3 border border-gray-200'}`} data-testid="wida-composite-bar">
      <div className={`${compact ? 'w-8 h-8 text-sm' : 'w-10 h-10 text-base'} ${color.bg} ${color.text} flex items-center justify-center font-black flex-shrink-0`} style={{ fontFamily: 'Outfit, sans-serif' }}>
        {lvl}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between mb-0.5">
          <span className={`${compact ? 'text-xs' : 'text-sm'} font-bold ${color.text}`}>{name}</span>
          <span className="text-[10px] text-gray-400">Level {lvl}/6</span>
        </div>
        <div className={`w-full ${compact ? 'h-1' : 'h-1.5'} bg-gray-200`}>
          <div className={`h-full transition-all duration-500`} style={{ width: `${pct}%`, backgroundColor: color.ring }} />
        </div>
      </div>
    </div>
  );
};

export default ProficiencyLevelRings;
